import axios, { AxiosResponse } from "axios";
import {
    DataQualityIssue,
    DataQualityResponse,
    DetailedOrderReportResponse,
    ReportArtifactResponse,
    ReportFilters,
    ReportPageParams,
    ReportRole,
    RolePerformanceResponse,
    RolePerformanceDrilldownResponse,
    RolePerformanceMetric,
    OrderDashboardResponse,
    ProductOrderDetailsResponse,
    ReportCalendarMonthResponse,
    ReportCalendarResponse,
    ReportCalendarType,
    ReportTimelineResponse,
    SalesReportResponse,
} from "../types/models/Reporting";
import { API_ORDER_REPORTS_URL } from "../utils/constants";
import { getLocalToken, handleServiceError } from "../utils/helpers";

type QueryValue = string | number | boolean | undefined | null;

const compactParams = (params: object) => Object.fromEntries(
    Object.entries(params as Record<string, QueryValue>)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
);

const authorizedHeaders = () => ({
    Authorization: `Bearer ${getLocalToken()}`,
});

const get = async <T,>(path: string, params: object): Promise<AxiosResponse<T>> => {
    try {
        return await axios.get<T>(`${API_ORDER_REPORTS_URL}${path}`, {
            headers: authorizedHeaders(),
            params: compactParams(params),
        });
    } catch (error: any) {
        handleServiceError(error);
        throw error;
    }
};

export const getOrderDashboard = (filters: ReportFilters) => {
    const { attentionOnly, scope, ...dashboardFilters } = filters;
    return get<OrderDashboardResponse>("/dashboard", dashboardFilters);
};

export const getDetailedOrderReport = (
    filters: ReportFilters,
    page: ReportPageParams
) => get<DetailedOrderReportResponse>("/detailed", { ...filters, ...page });

export const getRolePerformance = (
    role: ReportRole,
    filters: ReportFilters,
    params: ReportPageParams & { search?: string; active?: boolean; orderSearch?: string }
) => {
    const { search: filteredOrderSearch, attentionOnly, ...sharedFilters } = filters;
    return get<RolePerformanceResponse>(`/performance/${role}`, {
        ...sharedFilters,
        ...params,
        orderSearch: params.orderSearch || filteredOrderSearch,
    });
};

export const getRolePerformanceDrilldown = (
    role: ReportRole,
    userId: string,
    metric: RolePerformanceMetric,
    filters: ReportFilters,
    page: ReportPageParams
) => {
    const { search, attentionOnly, ...sharedFilters } = filters;
    return get<RolePerformanceDrilldownResponse>(
        `/performance/${role}/${userId}/drilldown`,
        {
            ...sharedFilters,
            ...page,
            metric,
            orderSearch: search,
        }
    );
};

export const getDataQuality = (
    issue: DataQualityIssue,
    filters: ReportFilters,
    page: ReportPageParams
) => {
    const { attentionOnly, ...qualityFilters } = filters;
    return get<DataQualityResponse>(`/data-quality/${issue}`, { ...qualityFilters, ...page });
};

export const getProductOrderDetails = (
    productId: string,
    filters: ReportFilters,
    page: ReportPageParams
) => {
    const { attentionOnly, ...productFilters } = filters;
    return get<ProductOrderDetailsResponse>(`/products/${productId}`, {
        ...productFilters,
        ...page,
    });
};

export const getSalesOrderReport = (
    filters: ReportFilters,
    page: ReportPageParams,
    origin?: string
) => {
    const { attentionOnly, status, ...salesFilters } = filters;
    return get<SalesReportResponse>("/sales", { ...salesFilters, ...page, origin });
};

const calendarFilters = (type: ReportCalendarType, filters: ReportFilters) => {
    const { from, to, ...withoutDateRange } = filters;
    if (type === "SALES") {
        const { attentionOnly, status, ...salesFilters } = withoutDateRange;
        return salesFilters;
    }
    return withoutDateRange;
};

export const getReportCalendar = (
    type: ReportCalendarType,
    year: number,
    filters: ReportFilters,
    origin?: string
) => get<ReportCalendarResponse>(`/calendar/${type}`, {
    ...calendarFilters(type, filters),
    year,
    origin,
});

export const getReportCalendarMonth = (
    type: ReportCalendarType,
    year: number,
    month: number,
    filters: ReportFilters,
    page: ReportPageParams,
    origin?: string
) => get<ReportCalendarMonthResponse>(`/calendar/${type}/${year}/${month}`, {
    ...calendarFilters(type, filters),
    ...page,
    origin,
});

export const getReportTimeline = (
    type: ReportCalendarType,
    filters: ReportFilters,
    origin?: string
) => get<ReportTimelineResponse>(`/calendar/${type}/timeline`, {
    ...calendarFilters(type, filters),
    origin,
});

export const exportDetailedOrderReport = async (
    filters: ReportFilters,
    sort: string
): Promise<AxiosResponse<ReportArtifactResponse>> => {
    try {
        return await axios.post<ReportArtifactResponse>(
            `${API_ORDER_REPORTS_URL}/detailed/export`,
            null,
            {
                headers: authorizedHeaders(),
                params: compactParams({ ...filters, sort }),
            }
        );
    } catch (error: any) {
        handleServiceError(error);
        throw error;
    }
};

export const exportSalesOrderReport = async (
    filters: ReportFilters,
    sort: string,
    origin?: string
): Promise<AxiosResponse<ReportArtifactResponse>> => {
    const { attentionOnly, status, ...salesFilters } = filters;
    try {
        return await axios.post<ReportArtifactResponse>(
            `${API_ORDER_REPORTS_URL}/sales/export`,
            null,
            {
                headers: authorizedHeaders(),
                params: compactParams({ ...salesFilters, sort, origin }),
            }
        );
    } catch (error: any) {
        handleServiceError(error);
        throw error;
    }
};

export const downloadReportArtifact = async (artifact: ReportArtifactResponse) => {
    const apiUrl = new URL(API_ORDER_REPORTS_URL);
    const downloadUrl = new URL(artifact.downloadUrl);
    if (downloadUrl.origin !== apiUrl.origin) {
        const link = document.createElement("a");
        link.href = artifact.downloadUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
    }

    try {
        const response = await axios.get(artifact.downloadUrl, {
            headers: authorizedHeaders(),
            responseType: "blob",
        });
        const objectUrl = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = artifact.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
    } catch (error: any) {
        handleServiceError(error);
    }
};
